import type { QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { ApiHttpError, apiFetch } from '@/lib/api'
import { markLoggedOut } from '@/lib/auth'
import type { PendingEvent } from '@/lib/db'

import {
  backoffReadyAt,
  bumpAttempt,
  getPendingEvents,
  markDead,
  removePendingEvent,
} from './queue'
import { WORK_SESSION_REQUEST_TIMEOUT_MS } from './use-work-sessions'

let inflight: Promise<void> | null = null

/**
 * Jedyne odpowiedzi, po których backend na pewno nie zmieni zdania. Cała reszta
 * (404/405 z proxy przy restarcie kontenera, 408, 429, 5xx) bywa chwilowa —
 * oznaczenie zdarzenia jako martwego wyrzucałoby kliknięcie na zawsze.
 */
const PERMANENT_REJECTION = new Set([400, 409, 422])

function clockLabel(ts: number): string {
  const d = new Date(ts)
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  return `${day}.${month} ${hh}:${mm}`
}

function describeDropped(ev: PendingEvent, err: ApiHttpError): string {
  const what = ev.type === 'start' ? 'Rozpoczęcie pracy' : 'Zakończenie pracy'
  return `${what} z ${clockLabel(ev.occurredAt)} — ${err.message}`
}

export interface FlushOptions {
  /** Zaraz po zalogowaniu czekanie na backoff tylko przedłuża blokadę przycisku. */
  ignoreBackoff?: boolean
}

/**
 * Wysyła zaległe zdarzenia zapisane offline przez poprzednią wersję aplikacji.
 * Nowe kliknięcia już tu nie trafiają — to wyłącznie opróżnianie kolejki.
 * Wołane z SyncController: po zalogowaniu, przy powrocie sieci/karty i cyklicznie.
 *
 * Kolejka jest UPORZĄDKOWANYM logiem przyczynowym: `start` musi dojść do API
 * przed swoim `end`. Dlatego każde zdarzenie, którego nie da się teraz wysłać,
 * blokuje wszystkie następne (`break`, nigdy `continue`) — inaczej `end`
 * wyprzedziłby `start`, dostał 409 „nie masz otwartej sesji" i wypadł z kolejki
 * razem z godziną wyjścia, którą cała ta funkcja ma chronić. Z tego samego
 * powodu ekran główny blokuje przycisk, dopóki w kolejce cokolwiek żyje.
 *
 * Reguły:
 *  - `occurredAt` idzie zawsze — backend zapisuje czas KLIKNIĘCIA, nie czas synchronizacji.
 *  - backoff niegotowy → przerwij (zdarzenie z przodu blokuje log).
 *  - 401 → przerwij bez liczenia próby; sesja = null, więc RequireAuth prosi
 *    o logowanie, a po nim SyncController od razu ponawia wysyłkę.
 *  - 400/409/422 → backend nie zmieni zdania; oznacz jako martwe (zostaje
 *    w IndexedDB jako ślad z godziną i GPS) i idź dalej — inaczej jeden trwale
 *    odrzucony wpis blokowałby kolejkę na zawsze.
 *  - każdy inny status i brak odpowiedzi → bump + przerwij, wykładniczy backoff
 *    w `backoffReadyAt`.
 */
export async function flushQueue(
  qc: QueryClient,
  options: FlushOptions = {},
): Promise<void> {
  if (inflight) return inflight
  inflight = (async () => {
    const pending = await getPendingEvents()
    if (pending.length === 0) return

    const now = Date.now()
    const synced: number[] = []
    const dropped: string[] = []

    for (const ev of pending) {
      // `break`, nie `continue` — patrz komentarz nad funkcją. Zdarzenie
      // w backoffie musi zatrzymać cały log, żeby nie odwrócić kolejności.
      if (!options.ignoreBackoff && backoffReadyAt(ev) > now) break

      try {
        await apiFetch(`/api/work-sessions/${ev.type}`, {
          method: 'POST',
          body: {
            clientEventId: ev.clientEventId,
            lat: ev.payload.lat,
            lng: ev.payload.lng,
            accuracyM: ev.payload.accuracyM,
            occurredAt: new Date(ev.occurredAt).toISOString(),
          },
          timeoutMs: WORK_SESSION_REQUEST_TIMEOUT_MS,
        })
        synced.push(ev.id)
      } catch (err) {
        if (err instanceof ApiHttpError) {
          if (err.status === 401) {
            markLoggedOut(qc)
            break
          }
          if (PERMANENT_REJECTION.has(err.status)) {
            await markDead(ev.id, `HTTP ${err.status}: ${err.message}`)
            dropped.push(describeDropped(ev, err))
            continue
          }
          await bumpAttempt(ev.id, `HTTP ${err.status}: ${err.message}`)
          break
        }
        await bumpAttempt(ev.id, err instanceof Error ? err.message : String(err))
        break
      }
    }

    if (synced.length > 0) {
      // Najpierw nowy stan z serwera, potem zdjęcie zdarzeń z kolejki. Odwrotnie
      // przycisk na moment odblokowywał się na starym `/me` (np. „Zaczynam pracę"
      // przy właśnie wysłanym starcie) i stuknięcie kończyło się 409. Ponowna
      // wysyłka po ubiciu aplikacji w tej chwili jest bezpieczna — backend
      // rozpoznaje `clientEventId`.
      //
      // Najpierw anulowanie: pierwsze pobranie `/me` (start aplikacji, świeżo
      // po zalogowaniu) mogło wyjść PRZED wysyłką. Zapytanie bez danych
      // ignoruje `cancelRefetch`, więc samo unieważnienie podpinało się pod
      // tę starą odpowiedź i przycisk odblokowywał się na stanie sprzed zapisu.
      // `revert` cofa je do „bez danych" i unieważnienie pyta serwer od nowa.
      void qc.invalidateQueries({ queryKey: ['my-monthly-progress'] })
      await qc.cancelQueries({ queryKey: ['work-sessions'] })
      await qc.invalidateQueries({ queryKey: ['work-sessions'] })
      for (const id of synced) await removePendingEvent(id)
      toast.success(
        synced.length === 1
          ? 'Zaległy zapis z tego urządzenia został wysłany'
          : `Wysłano zaległe zapisy z tego urządzenia: ${synced.length}`,
      )
    }

    for (const message of dropped) {
      toast.error(
        `Nie udało się zapisać: ${message}. Zgłoś to administratorowi — poprawi wpis ręcznie.`,
        { duration: 12_000 },
      )
    }
  })()
  try {
    await inflight
  } finally {
    inflight = null
  }
}
